'use client';

import { ChevronLeft, ChevronRight } from 'lucide-react';
import type * as React from 'react';
import {
	type SidebarAccent,
	SidebarBadge,
	type SidebarBadgeTone,
	SidebarGroup,
	SidebarItem,
	SidebarKpi,
} from '../../atoms/sidebar/sidebar';
import { Text } from '../../atoms/text/text';
import { cn } from '../../lib/cn';
import { type LinkLike, NativeLink } from '../../lib/link';

/**
 * Navigazione laterale condivisa da SLIMS CER e SLIMS PRO. La struttura è quella
 * di PRO — gruppi etichettati, icone monocromatiche, KPI in fondo — mentre la tinta
 * d'accento resta separata (`accent` ciano, `brand` per il chrome PRO).
 * Comprimi/espandi e copyright sono opzionali: li usa solo CER.
 *
 * Le misure vengono dal mock `SLIMS-CER_v11.html`: 248px aperta e 64px compressa,
 * 16px di padding verticale, icone 18px (20px da compressa), toggle a 12px.
 */

export interface SidebarNavBadge {
	text: string;
	tone?: SidebarBadgeTone;
}

export interface SidebarNavItem {
	/** Identificatore della voce; se assente si usa `href`. */
	id?: string;
	/** Testo della voce. */
	label: string;
	/** URL di destinazione: rende un link. In alternativa usa `onNavigate` con `id`. */
	href?: string;
	/** Icona monocromatica (lucide) dimensionata dal chiamante. */
	icon?: React.ReactNode;
	/** Contatore/etichetta a destra del testo. */
	badge?: SidebarNavBadge | null;
}

export interface SidebarNavGroup {
	/** Etichetta maiuscola mostrata sopra le voci del gruppo. */
	label: string;
	items: SidebarNavItem[];
}

/**
 * Voce della navigazione: sciolta (nessun raggruppamento) oppure gruppo
 * etichettato. Le due forme si mescolano liberamente nella stessa lista — CER
 * usa solo voci sciolte, PRO solo gruppi.
 */
export type SidebarNavEntry = SidebarNavItem | SidebarNavGroup;

function isGroup(entry: SidebarNavEntry): entry is SidebarNavGroup {
	return 'items' in entry;
}

export interface SidebarNavKpi {
	label: string;
	/** Valore già formattato; vuoto → em dash. */
	value?: string;
	detail?: React.ReactNode;
}

export interface SidebarNavigationProps {
	/** Voci di navigazione, in ordine: sciolte e/o raggruppate. */
	items: SidebarNavEntry[];
	/** Id (o href) della voce attiva. Con `matchPrefix` vince la corrispondenza di prefisso più lunga. */
	activeId: string;
	/**
	 * Considera attiva anche una voce il cui `href` è prefisso di `activeId`
	 * (navigazione per rotte: `/members` resta attivo su `/members/1/details`).
	 */
	matchPrefix?: boolean;
	/** Chiamata al click su una voce senza `href`, con il suo id. */
	onNavigate?: (id: string) => void;
	/** Tinta d'accento dell'interfaccia. Default: `accent`. */
	accent?: SidebarAccent;
	/** KPI sintetici ancorati in fondo alla sidebar. */
	kpis?: SidebarNavKpi[];
	/** Se true la sidebar è compressa (solo icone). */
	collapsed?: boolean;
	/** Se presente mostra il bottone comprimi/espandi in fondo. */
	onToggleCollapse?: () => void;
	/** Se impostato, mostra una fascia di copyright in fondo: "© <anno> <copyrightName>". */
	copyrightName?: string;
	/** Componente link da usare (default: link nativo `<a>`); passare il `Link` del router quando serve navigazione client-side. */
	linkComponent?: LinkLike;
}

const COLLAPSE_HOVER: Record<SidebarAccent, string> = {
	accent: 'hover:text-accent',
	brand: 'hover:text-brand',
};

/** Vero se `key` corrisponde alla voce attiva (uguaglianza o prefisso di rotta). */
function isActive(key: string, activeId: string, matchPrefix: boolean) {
	if (key === activeId) return true;
	return matchPrefix && activeId.startsWith(`${key}/`);
}

/**
 * Normalizza la lista in blocchi renderizzabili: le voci sciolte consecutive
 * confluiscono in un unico blocco senza intestazione, così la spaziatura resta
 * identica a quella dei gruppi etichettati.
 */
function toBlocks(entries: SidebarNavEntry[]): { label?: string; items: SidebarNavItem[] }[] {
	const blocks: { label?: string; items: SidebarNavItem[] }[] = [];
	for (const entry of entries) {
		if (isGroup(entry)) {
			blocks.push(entry);
			continue;
		}
		const last = blocks.at(-1);
		// I gruppi hanno sempre un'etichetta: `label === undefined` identifica un
		// blocco sciolto creato qui, quindi non muta mai gli array del chiamante.
		if (last && last.label === undefined) last.items.push(entry);
		else blocks.push({ items: [entry] });
	}
	return blocks;
}

export function SidebarNavigation({
	items,
	activeId,
	matchPrefix = false,
	onNavigate,
	accent = 'accent',
	kpis,
	collapsed = false,
	onToggleCollapse,
	copyrightName,
	linkComponent: Link = NativeLink,
}: SidebarNavigationProps) {
	const blocks = toBlocks(items);

	// Con più voci corrispondenti per prefisso vince la più specifica, altrimenti
	// `/cer` e `/cer/users` risulterebbero attive insieme.
	const activeKey = blocks
		.flatMap((b) => b.items.map((item) => item.id ?? item.href ?? item.label))
		.filter((key) => isActive(key, activeId, matchPrefix))
		.sort((a, b) => b.length - a.length)[0];

	return (
		<aside
			className={cn(
				'flex h-full flex-col overflow-hidden border-r border-border bg-panel transition-all duration-300',
				collapsed ? 'w-16' : 'w-[248px]',
			)}
		>
			<div
				className={cn(
					'min-h-0 flex-1 overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
					collapsed ? 'py-3' : 'py-4',
				)}
			>
				{blocks.map((block, bi) => (
					<SidebarGroup
						key={block.label ?? `block-${bi}`}
						collapsed={collapsed}
						label={collapsed ? undefined : block.label}
					>
						{block.items.map((item) => {
							const key = item.id ?? item.href ?? item.label;
							return (
								<SidebarItem
									key={key}
									label={item.label}
									href={item.href}
									linkComponent={Link}
									accent={accent}
									active={key === activeKey}
									collapsed={collapsed}
									title={collapsed ? item.label : undefined}
									onClick={item.href ? undefined : () => onNavigate?.(key)}
									icon={
										<span
											className={cn(
												'relative flex items-center justify-center [&_svg]:shrink-0',
												collapsed
													? 'h-5 w-5 [&_svg]:size-5'
													: 'h-[18px] w-[18px] [&_svg]:size-[18px]',
											)}
										>
											{item.icon}
											{item.badge && collapsed && (
												<span className="absolute -right-1.5 -top-1.5 h-1.5 w-1.5 rounded-full bg-warn" />
											)}
										</span>
									}
									badge={
										item.badge ? (
											<SidebarBadge tone={item.badge.tone ?? 'amber'}>
												{item.badge.text}
											</SidebarBadge>
										) : undefined
									}
								/>
							);
						})}
					</SidebarGroup>
				))}
			</div>

			{kpis && kpis.length > 0 && !collapsed ? (
				<div className="flex-none px-3.5 py-2.5">
					{kpis.map((k) => (
						<SidebarKpi key={k.label} label={k.label} value={k.value} detail={k.detail} />
					))}
				</div>
			) : null}

			{onToggleCollapse && (
				<button
					type="button"
					onClick={onToggleCollapse}
					className={cn(
						'flex flex-none items-center justify-center gap-2 border-t border-border py-3 text-text-muted transition-colors',
						COLLAPSE_HOVER[accent],
					)}
					title={collapsed ? 'Espandi pannello' : 'Comprimi pannello'}
				>
					{collapsed ? (
						<ChevronRight className="h-4 w-4" />
					) : (
						<>
							<ChevronLeft className="h-4 w-4" />
							<Text as="span" variant="body" tone="inherit">
								Comprimi pannello
							</Text>
						</>
					)}
				</button>
			)}

			{copyrightName && (
				<div className="flex-none border-t border-border px-2 py-1.5 text-center">
					<Text as="span" variant="caption" tone="muted" className="leading-none">
						{collapsed
							? `© ${new Date().getFullYear()}`
							: `© ${new Date().getFullYear()} ${copyrightName}`}
					</Text>
				</div>
			)}
		</aside>
	);
}
